const abortError = () => {
  const error = new Error('La carga del juego fue cancelada.');
  error.name = 'AbortError';
  return error;
};

const decodeImage = async (image) => {
  if (typeof image.decode !== 'function') return;

  // Safari has occasionally left decode() pending even after load. The load
  // event is already a safe fallback, so decoding only gets a short window.
  let timeoutId;
  try {
    await Promise.race([
      image.decode(),
      new Promise((resolve) => { timeoutId = window.setTimeout(resolve, 3000); }),
    ]);
  } catch (_) {
    // Some browsers reject decode() for otherwise valid cached images.
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const preloadImageAssets = async (assets, { signal, onProgress, timeoutMs = 15000 } = {}) => {
  const seenUrls = new Set();
  const uniqueAssets = assets.filter((asset) => {
    const url = typeof asset === 'string' ? asset : asset.url;
    if (seenUrls.has(url)) return false;
    seenUrls.add(url);
    return true;
  });
  const total = uniqueAssets.length;
  let loaded = 0;

  const entries = await Promise.all(uniqueAssets.map((asset) => new Promise((resolve, reject) => {
    const url = typeof asset === 'string' ? asset : asset.url;
    const image = new Image();
    let timeoutId;
    let settled = false;

    const cleanup = () => {
      settled = true;
      window.clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', handleAbort);
    };
    const handleAbort = () => {
      cleanup();
      image.src = '';
      reject(abortError());
    };

    image.onload = async () => {
      await decodeImage(image);
      if (settled) return;
      if (signal?.aborted) {
        handleAbort();
        return;
      }
      cleanup();
      loaded += 1;
      onProgress?.({ loaded, total, url });
      resolve([url, image]);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`No se pudo cargar un recurso visual: ${url}`));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }
    signal?.addEventListener('abort', handleAbort, { once: true });
    image.decoding = 'async';
    if (typeof asset !== 'string' && asset.priority) image.fetchPriority = asset.priority;
    timeoutId = window.setTimeout(() => {
      cleanup();
      image.src = '';
      reject(new Error(`Un recurso visual tardó demasiado en responder: ${url}`));
    }, timeoutMs);
    image.src = url;
  })));

  return new Map(entries);
};
